import React, { useState, useEffect, useRef } from 'react';
import { generateSegmentationTrees } from '../utils/api';
import { saveToStorage, loadFromStorage, StorageKeys, clearAllStorage } from '../utils/storage';

const treeColors = {
  'CUSTOMER TYPE': 'primary',
  'PROBLEM/PAIN POINT': 'sage', 
  'SOLUTION DELIVERY METHOD': 'cream',
  'BUSINESS MODEL': 'primary',
  'GEOGRAPHY/SCALE': 'sage',
  'TECHNOLOGY/FEATURE': 'cream'
};

const TreeVisualization = ({ confirmedMarket, onGenerateReport, onResetToStart }) => {
  const [trees, setTrees] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadTreesCalled = useRef(false);

  const colorClasses = {
    primary: 'border-primary-300 bg-primary-50 text-primary-900',
    sage: 'border-sage-300 bg-sage-50 text-sage-900',
    cream: 'border-cream-300 bg-cream-50 text-cream-900'
  };

  const loadTrees = async () => {
    console.log('🚀 loadTrees called with confirmedMarket:', confirmedMarket);
    console.log('🔄 isLoading state:', isLoading);
    console.log('🔄 loadTreesCalled.current:', loadTreesCalled.current);

    if (isLoading || loadTreesCalled.current) {
      console.log('⚠️ Already loading or called, skipping...');
      return; // Prevent duplicate calls
    }

    if (!confirmedMarket) {
      console.log('⚠️ No confirmed market provided, skipping...');
      return;
    }

    loadTreesCalled.current = true;
    
    setIsLoading(true);
    setError(null);

    try {
      console.log('💾 Checking for cached trees...');
      const savedTrees = loadFromStorage(StorageKeys.SEGMENTATION_TREES);
      console.log('💾 Cached trees found:', !!savedTrees);
      
      if (savedTrees && savedTrees.marketId === confirmedMarket) {
        console.log('✅ Using cached trees for market:', confirmedMarket);
        console.log('📊 Cached trees data:', savedTrees.trees);
        setTrees(savedTrees.trees);
        
        // Initialize selection state
        const initialSelection = {};
        Object.keys(savedTrees.trees).forEach(approach => {
          initialSelection[approach] = [];
        });
        setSelectedCategories(initialSelection);
        console.log('✅ Loaded cached trees successfully');
      } else {
        console.log('🔄 Generating new trees for market:', confirmedMarket);
        console.log('🌐 Calling generateSegmentationTrees API...');
        
        const rawTrees = await generateSegmentationTrees(confirmedMarket);
        console.log('📥 Raw API response received:');
        console.log('📥 Response type:', typeof rawTrees);
        console.log('📥 Response length:', rawTrees?.length);
        console.log('📥 First 500 chars:', rawTrees?.substring(0, 500));
        console.log('📥 Full response:', rawTrees);
        
        console.log('🔍 Parsing trees...');
        const parsedTrees = parseTreeResponse(rawTrees);
        console.log('📊 Parsed trees result:', parsedTrees);
        console.log('📊 Number of approaches parsed:', Object.keys(parsedTrees).length);
        
        if (Object.keys(parsedTrees).length === 0) {
          console.error('❌ No trees were parsed from the response!');
          throw new Error('Failed to parse segmentation trees from API response');
        }
        
        setTrees(parsedTrees);
        console.log('💾 Saving to storage...');
        saveToStorage(StorageKeys.SEGMENTATION_TREES, {
          marketId: confirmedMarket,
          trees: parsedTrees,
          timestamp: Date.now()
        });
        
        // Initialize selection state
        const initialSelection = {};
        Object.keys(parsedTrees).forEach(approach => {
          initialSelection[approach] = [];
        });
        setSelectedCategories(initialSelection);
        console.log('✅ Generated and saved new trees successfully');
      }
    } catch (err) {
      console.error('❌ Error in loadTrees:');
      console.error('❌ Error message:', err.message);
      console.error('❌ Error stack:', err.stack);
      console.error('❌ Full error object:', err);
      setError(err.message || 'Failed to generate segmentation trees');
      loadTreesCalled.current = false; // Reset on error so retry is possible
    } finally {
      console.log('🏁 Setting isLoading to false');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log('🎯 useEffect triggered for confirmedMarket:', confirmedMarket);
    console.log('🎯 Resetting loading state...');
    setIsLoading(false);
    setError(null);
    setTrees(null);
    loadTreesCalled.current = false; // Reset the flag for new market

    // Small delay to ensure state is reset
    const timeoutId = setTimeout(() => {
      loadTrees();
    }, 100);

    const savedSelected = loadFromStorage(StorageKeys.SELECTED_SEGMENTS, {});
    if (savedSelected && typeof savedSelected === 'object') {
      // Convert old format to new array format if needed
      const convertedSelected = {};
      Object.entries(savedSelected).forEach(([approach, value]) => {
        if (Array.isArray(value)) {
          convertedSelected[approach] = value;
        } else if (value && typeof value === 'string') {
          convertedSelected[approach] = [value];
        } else {
          convertedSelected[approach] = [];
        }
      });
      setSelectedCategories(convertedSelected);
    } else {
      setSelectedCategories({});
    }

    // Cleanup function to cancel timeout if component unmounts
    return () => clearTimeout(timeoutId);
  }, [confirmedMarket]); // Only depend on confirmedMarket, not loadTrees

  const parseTreeResponse = (rawResponse) => {
    console.log('🔍 === PARSING TREE RESPONSE ===');
    console.log('🔍 Raw response type:', typeof rawResponse);
    console.log('🔍 Raw response length:', rawResponse?.length);
    console.log('🔍 Raw response preview:', rawResponse?.substring(0, 200));
    
    const trees = {};
    
    // Try parsing expected format first (with **APPROACH X:)
    console.log('🔍 Trying **APPROACH X: format...');
    let approaches = rawResponse.split(/\*\*APPROACH\s*\d+:/i);
    console.log('🔍 Split by **APPROACH resulted in', approaches.length, 'sections');
    
    // If that doesn't work, try markdown format (## X.)
    if (approaches.length <= 1) {
      console.log('🔍 Trying ## X. format...');
      approaches = rawResponse.split(/##\s*\d+\.\s*/);
      console.log('🔍 Split by ## resulted in', approaches.length, 'sections');
    }
    
    // Also try generic numbered sections
    if (approaches.length <= 1) {
      console.log('🔍 Trying generic numbered sections...');
      approaches = rawResponse.split(/\n(?=\d+\.)/);
      console.log('🔍 Split by numbered sections resulted in', approaches.length, 'sections');
    }
    
    console.log('🔍 Processing', approaches.length, 'sections...');
    
    approaches.forEach((section, index) => {
      console.log(`🔍 === SECTION ${index} ===`);
      console.log(`🔍 Section ${index} content:`, section.substring(0, 300));
      
      if (index === 0) {
        console.log(`🔍 Skipping first section (usually empty)`);
        return; // Skip first empty section
      }
      
      const lines = section.trim().split('\n');
      console.log(`🔍 Section ${index} has ${lines.length} lines`);
      
      if (lines.length === 0) {
        console.log(`🔍 Section ${index} is empty, skipping`);
        return;
      }
      
      // Extract approach name from first line
      const firstLine = lines[0].trim();
      console.log(`🔍 First line: "${firstLine}"`);
      
      let approachName = '';
      
      // Try different patterns to extract approach name
      const approachMatch = firstLine.match(/BY\s+(.+?)\*\*/i) || 
                          firstLine.match(/BY\s+(.+)/i) ||
                          firstLine.match(/(.+?)\s*Segmentation/i) ||
                          firstLine.match(/(.+)/);
      
      if (approachMatch) {
        approachName = approachMatch[1].trim().replace(/\*\*/g, '');
        console.log(`🔍 Extracted approach name: "${approachName}"`);
      } else {
        console.log(`🔍 Could not extract approach name from: "${firstLine}"`);
      }
      
      if (!approachName) {
        console.log(`🔍 No approach name found, skipping section ${index}`);
        return;
      }
      
      const tree = {};
      let currentCategory = null;
      
      console.log(`🔍 Processing ${lines.length} lines for approach "${approachName}"`);
      
      lines.slice(1).forEach((line, lineIndex) => {
        const originalLine = line;
        line = line.trim();
        
        console.log(`🔍   Line ${lineIndex}: "${originalLine}" (trimmed: "${line}")`);
        
        if (!line || line.startsWith('#')) {
          console.log(`🔍   Skipping empty or header line`);
          return;
        }
        
        // Main category: starts with '- ' and is NOT indented, or starts with **
        if ((originalLine.match(/^-\s/) && !originalLine.match(/^\s/)) || originalLine.match(/^\*\*[^*]+\*\*:/)) {
          currentCategory = line
            .replace(/^-\s+/, '')
            .replace(/^\*\*([^*]+)\*\*:/, '$1')
            .replace(/\*\*/g, '')
            .trim();
          if (currentCategory) {
            tree[currentCategory] = [];
            console.log(`🔍   ✅ Found main category: "${currentCategory}"`);
          }
        } 
        // Subcategory: line starts with spaces followed by '- '
        else if (originalLine.match(/^\s+- /) && currentCategory) {
          const subcategory = line
            .replace(/^-\s+/, '')
            .replace(/\*\*/g, '')
            .trim();
          if (subcategory) {
            tree[currentCategory].push(subcategory);
            console.log(`🔍   ✅ Added subcategory "${subcategory}" to "${currentCategory}"`);
          }
        } else {
          console.log(`🔍   ⚠️ Line didn't match any pattern`);
        }
      });
      
      // Debug output
      console.log(`🔍 Approach "${approachName}" final tree:`, tree);
      console.log(`🔍 Approach "${approachName}" has ${Object.keys(tree).length} categories`);
      
      if (Object.keys(tree).length > 0) {
        trees[approachName] = tree;
        console.log(`🔍 ✅ Added approach "${approachName}" to final trees`);
      } else {
        console.log(`🔍 ❌ No categories found for approach "${approachName}"`);
      }
    });
    
    console.log('🔍 === FINAL RESULT ===');
    console.log('🔍 Total approaches parsed:', Object.keys(trees).length);
    console.log('🔍 Final trees object:', trees);
    
    return trees;
  };

  const handleCategorySelection = (approachName, categoryName) => {
    setSelectedCategories(prev => {
      const currentSelected = prev || {};
      // Toggle selection: if already selected, deselect; otherwise select
      const newSelected = {
        ...currentSelected,
        [approachName]: currentSelected[approachName] === categoryName ? null : categoryName
      };
      saveToStorage(StorageKeys.SELECTED_SEGMENTS, newSelected);
      return newSelected;
    });
  };

  const handleSubcategorySelection = (approachName, categoryName, subcategory) => {
    console.log('🎯 Subcategory selected:', { approachName, categoryName, subcategory });
    setSelectedCategories(prev => {
      const currentSelected = prev || {};
      const currentSelections = currentSelected[approachName] || [];
      const selectionKey = `${categoryName}:${subcategory}`;
      
      // Toggle selection - if already selected, remove it; otherwise add it
      const newSelections = currentSelections.includes(selectionKey)
        ? currentSelections.filter(item => item !== selectionKey)
        : [...currentSelections, selectionKey];
      
      const newSelected = {
        ...currentSelected,
        [approachName]: newSelections
      };
      saveToStorage(StorageKeys.SELECTED_SEGMENTS, newSelected);
      console.log('🎯 Updated selection:', newSelected);
      return newSelected;
    });
  };

  const getSelectedSegments = () => {
    const segments = [];
    if (!selectedCategories || !trees) return segments;
    
    Object.entries(selectedCategories).forEach(([approach, selectedValues]) => {
      if (Array.isArray(selectedValues)) {
        selectedValues.forEach(selectedValue => {
          if (selectedValue && selectedValue.includes(':')) {
            const [categoryName, subcategory] = selectedValue.split(':');
            if (trees[approach] && trees[approach][categoryName]) {
              segments.push({
                approach,
                category: categoryName,
                subcategory,
                type: 'subcategory'
              });
            }
          }
        });
      }
    });
    return segments;
  };

  const handleGenerateReport = () => {
    const segments = getSelectedSegments();
    if (segments.length === 0) return;
    
    saveToStorage(StorageKeys.CURRENT_STEP, 5);
    onGenerateReport(segments);
  };

  const retryGeneration = () => {
    setTrees(null);
    setError(null);
    loadTreesCalled.current = false; // Reset the flag to allow retry
    // Clear localStorage to force fresh generation
    localStorage.removeItem(StorageKeys.SEGMENTATION_TREES);
    loadTrees();
  };

  const handleClearHistory = () => {
    if (window.confirm('Are you sure you want to clear all data and return to the start?')) {
      clearAllStorage();
      onResetToStart();
    }
  };

  if (isLoading || (!trees && !error)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Generating Market Segments</h2>
          <p className="text-gray-600">
            Creating 6 different segmentation approaches for your market...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Generation Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={retryGeneration}
            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!trees || Object.keys(trees).length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Segments Generated</h2>
          <p className="text-gray-600 mb-6">Unable to generate market segments. Please try again.</p>
          <button
            onClick={retryGeneration}
            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200"
          >
            Retry Generation
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <button
        onClick={handleClearHistory}
        className="fixed top-4 right-4 px-3 py-1 text-xs font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors duration-200 z-20"
      >
        DEV: Clear History
      </button>
      <header className="bg-white border-b border-gray-200 p-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Market Segmentation Explorer</h2>
          <p className="text-gray-600 mb-4">
            Explore different ways to segment your market: <span className="font-medium">{confirmedMarket}</span>
          </p>
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">
              Select segments from different approaches to build your market strategy
            </p>
            <button
              onClick={handleGenerateReport}
              disabled={getSelectedSegments().length === 0}
              className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
                getSelectedSegments().length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg'
              }`}
            >
              Find pain points from Reddit ({Object.values(selectedCategories || {}).reduce((total, selections) => total + (Array.isArray(selections) ? selections.length : 0), 0)})
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
          {trees && Object.entries(trees).map(([approachName, tree]) => {
            const color = treeColors[approachName] || 'primary';
            const colorClass = colorClasses[color];

            return (
              <div key={approachName} className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                <div className={`border-l-4 pl-4 mb-6 ${colorClass.replace('border-', 'border-l-').replace('bg-', '').replace('text-', '')}`}>
                  <h3 className="font-bold text-lg text-gray-900">BY {approachName}</h3>
                </div>

                <div className="space-y-4">
                  {tree && Object.entries(tree).map(([categoryName, subcategories]) => {
                    const isCategorySelected = selectedCategories && selectedCategories[approachName] === categoryName;

                    return (
                      <div key={categoryName} className="space-y-2">
                        {/* Category Header - Non-selectable */}
                        <div className="bg-gray-100 px-4 py-2 rounded-lg border border-gray-200">
                          <h4 className="font-bold text-gray-800 text-sm uppercase tracking-wide">
                            {categoryName}
                          </h4>
                        </div>
                        
                        {/* Subcategories with radio buttons */}
                        <div className="pl-4 space-y-2">
                          {subcategories.map((subcategory, idx) => {
                            const selectedValues = selectedCategories && selectedCategories[approachName] || [];
                            const isSubcategorySelected = selectedValues.includes(`${categoryName}:${subcategory}`);
                            
                            return (
                              <label key={idx} className={`block p-3 rounded-lg cursor-pointer transition-all duration-200 border-2 ${
                                isSubcategorySelected
                                  ? `${colorClass} border-opacity-100`
                                  : 'bg-white hover:bg-gray-50 border-gray-200'
                              }`}>
                                <input
                                  type="checkbox"
                                  checked={isSubcategorySelected}
                                  onChange={() => handleSubcategorySelection(approachName, categoryName, subcategory)}
                                  className="sr-only"
                                />
                                <div className="text-sm text-gray-700">
                                  {subcategory}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {selectedCategories && Object.values(selectedCategories).some(selections => Array.isArray(selections) && selections.length > 0) && (
          <div className="mt-8 bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Selected Categories ({Object.values(selectedCategories || {}).reduce((total, selections) => total + (Array.isArray(selections) ? selections.length : 0), 0)})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
              {selectedCategories && Object.entries(selectedCategories)
                .filter(([_, selectedValues]) => Array.isArray(selectedValues) && selectedValues.length > 0)
                .flatMap(([approach, selectedValues]) =>
                  selectedValues.map(selectedValue => {
                    const [categoryName, subcategory] = selectedValue.split(':');
                    const color = treeColors[approach] || 'primary';
                    const colorClass = colorClasses[color];

                    return (
                      <div key={`${approach}-${selectedValue}`} className={`p-4 rounded-lg border-2 ${colorClass}`}>
                        <div className="text-xs font-medium opacity-75 mb-2">
                          {approach}
                        </div>
                        <div className="text-sm font-semibold mb-2">
                          {categoryName}
                        </div>
                        <div className="text-sm text-gray-800 font-medium">
                          {subcategory}
                        </div>
                        <button
                          onClick={() => handleSubcategorySelection(approach, categoryName, subcategory)}
                          className="mt-3 text-xs text-red-600 hover:text-red-800 font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    );
                  })
                )
              }
            </div>
            <div className="text-center">
              <button
                onClick={handleGenerateReport}
                className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Find pain points from Reddit
              </button>
            </div>
          </div>
        )}

        </div>
    </div>
  );
};

export default TreeVisualization;